// MyPledgeDetail — v2 port of v1's web/dashboard/src/pages/
// MyPledgeDetail.tsx. CHG-1519 / ITER-3638 (donor-portal wave 2).
//
// Donor self-service detail view for a single pledge. Consumes
// GET /api/v1/my-giving/pledges/:id (v1 portal.py) plus the
// /pay action (opens provider payment URL in a new tab),
// /timeline tab fetch, and /cancel action. PATCH /pledges/:id
// (edit due-date / amount) is queued as a follow-up — the brief's
// minimum is "view + pay + timeline + cancel".
//
// Mounts at /me/pledges/:id (v2-canonical URL scheme; backend
// endpoints stay at /api/v1/my-giving/*).

import type { ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DetailPage, type DetailPageConfig } from "../components/composition";
import {
  fetchPledgeDetail,
  fetchPledgeTimeline,
  payPledge,
  cancelPledge,
  type PortalPledgeDetail,
} from "../api/my-giving";

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "fulfilled"
      ? { bg: "#dcfce7", fg: "#166534" }
      : status === "in_progress" || status === "committed"
      ? { bg: "#dbeafe", fg: "#1e40af" }
      : status === "canceled" || status === "lapsed"
      ? { bg: "#fee2e2", fg: "#991b1b" }
      : { bg: "#e5e7eb", fg: "#374151" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 500,
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        textTransform: "capitalize",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        {label}
      </span>
      <span style={{ fontSize: 14, color: "var(--color-text)" }}>{value}</span>
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        position: "relative",
        width: "100%",
        height: 12,
        background: "var(--color-border-subtle, #e5e7eb)",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${clamped}%`,
          height: "100%",
          background: "var(--color-primary)",
          transition: "width 200ms ease",
        }}
      />
    </div>
  );
}

function TimelineSection({ pledgeId }: { pledgeId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["me", "pledge", "timeline", pledgeId] as const,
    queryFn: () => fetchPledgeTimeline(pledgeId),
  });
  if (isLoading) {
    return (
      <p
        data-testid="timeline-loading"
        style={{ fontSize: 13, color: "var(--color-text-muted)" }}
      >
        Loading timeline…
      </p>
    );
  }
  if (error || !data) {
    return (
      <p
        data-testid="timeline-error"
        style={{ fontSize: 13, color: "var(--color-text-muted)" }}
      >
        Timeline unavailable.
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <p
        data-testid="timeline-empty"
        style={{ fontSize: 13, color: "var(--color-text-muted)" }}
      >
        No timeline entries yet.
      </p>
    );
  }
  return (
    <ol
      data-testid="timeline-list"
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {data.map((entry, idx) => (
        <li
          key={`${entry.at}-${idx}`}
          style={{
            padding: 12,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md, 8px)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
            {entry.at} {entry.actor ? `· ${entry.actor}` : ""}
          </span>
          <span style={{ fontSize: 14, color: "var(--color-text)" }}>
            <strong style={{ textTransform: "capitalize" }}>
              {entry.kind.replace(/_/g, " ")}
            </strong>
            {entry.note ? ` — ${entry.note}` : ""}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function MyPledgeDetail() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["me", "pledge", "detail"] });
    queryClient.invalidateQueries({ queryKey: ["me", "pledges", "list"] });
  };

  const pay = useMutation({
    mutationFn: payPledge,
    onSuccess: (resp) => {
      if (resp?.payment_url) {
        // Provider checkout pages are heavy + require a real referrer;
        // open in a new tab so the donor keeps the portal context.
        window.open(resp.payment_url, "_blank", "noopener,noreferrer");
      }
    },
  });
  const cancel = useMutation({
    mutationFn: cancelPledge,
    onSuccess: invalidate,
  });

  const config: DetailPageConfig<PortalPledgeDetail> = {
    idParam: "pledgeId",
    queryKey: (id) => ["me", "pledge", "detail", id] as const,
    queryFn: (id) => fetchPledgeDetail(id),
    backTo: "/my-giving/pledges",
    title: (record) => {
      const p = record.pledge;
      const campaign = p.campaign_name ? ` — ${p.campaign_name}` : "";
      return `Pledge${campaign}`;
    },
    subtitle: (record) => record.pledge.org_name,
    actions: (record) => {
      const p = record.pledge;
      const acts = [];
      if (p.outstanding_minor_units > 0 && p.status !== "fulfilled" && p.status !== "canceled") {
        acts.push({
          id: "pay",
          label: pay.isPending ? "Loading…" : "Make a payment",
          variant: "primary" as const,
          onClick: () => pay.mutate(p.id),
          disabled: pay.isPending,
        });
      }
      if (p.status !== "fulfilled" && p.status !== "canceled") {
        acts.push({
          id: "cancel",
          label: cancel.isPending ? "Canceling…" : "Cancel pledge",
          variant: "secondary" as const,
          onClick: () => {
            if (
              window.confirm(
                "Cancel this pledge? Any outstanding amount will no longer be expected.",
              )
            ) {
              cancel.mutate(p.id);
            }
          },
          disabled: cancel.isPending,
        });
      }
      return acts;
    },
    tabs: [
      {
        id: "summary",
        label: "Summary",
        render: (record, fmt) => {
          const p = record.pledge;
          const pct =
            p.total_amount_minor_units > 0
              ? (p.fulfilled_minor_units / p.total_amount_minor_units) * 100
              : 0;
          // PLG-R17 / F19 — donor-facing reminder schedule (parity with the
          // v1 portal MyPledgeDetail + the mobile my_pledges view). schedule_label
          // (ACI cadence) + next_reminder_at come from the portal pledge read;
          // coarse recurring pledges fall back to the frequency word. Reminders
          // are notifications only — they never charge.
          const isRecurring = Boolean(p.frequency && p.frequency !== "one_time");
          const cadenceText: string | null = p.schedule_label
            ? String(p.schedule_label)
            : isRecurring
            ? String(p.frequency).replace(/_/g, " ")
            : null;
          const hasReminder = Boolean(cadenceText || p.next_reminder_at);
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                style={{
                  padding: 16,
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md, 8px)",
                }}
                data-testid="pledge-progress"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    marginBottom: 8,
                  }}
                >
                  <span>
                    {fmt.formatCurrency(p.fulfilled_minor_units, p.currency)} of{" "}
                    {fmt.formatCurrency(p.total_amount_minor_units, p.currency)}
                  </span>
                  <span style={{ fontWeight: 600, color: "var(--color-text)" }}>
                    {Math.round(pct)}%
                  </span>
                </div>
                <ProgressBar pct={pct} />
              </div>
              <div
                style={{
                  padding: 24,
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md, 8px)",
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 16,
                }}
              >
                <InfoRow label="Status" value={<StatusBadge status={p.status} />} />
                <InfoRow label="Organization" value={p.org_name} />
                <InfoRow label="Campaign" value={p.campaign_name ?? "—"} />
                {p.session_name && (
                  <InfoRow label="Given to" value={p.session_name} />
                )}
                <InfoRow
                  label="Outstanding"
                  value={
                    <span
                      style={{
                        color:
                          p.outstanding_minor_units > 0
                            ? "var(--color-warning, #b45309)"
                            : "var(--color-success, #166534)",
                        fontWeight: 600,
                      }}
                    >
                      {fmt.formatCurrency(p.outstanding_minor_units, p.currency)}
                    </span>
                  }
                />
                <InfoRow
                  label="Frequency"
                  value={
                    p.frequency ? (
                      <span style={{ textTransform: "capitalize" }}>
                        {p.frequency.replace(/_/g, " ")}
                      </span>
                    ) : (
                      "—"
                    )
                  }
                />
                <InfoRow
                  label="Start date"
                  value={
                    p.start_date ? fmt.formatDate(p.start_date, "medium") : "—"
                  }
                />
                <InfoRow
                  label="Due date"
                  value={p.due_date ? fmt.formatDate(p.due_date, "medium") : "—"}
                />
              </div>
              {hasReminder && (
                <div
                  data-testid="pledge-reminder-schedule"
                  style={{
                    padding: 24,
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md, 8px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--color-text)",
                    }}
                  >
                    Reminder schedule
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 16,
                    }}
                  >
                    <InfoRow
                      label="Cadence"
                      value={
                        cadenceText ? (
                          <span style={{ textTransform: "capitalize" }}>
                            {cadenceText}
                          </span>
                        ) : (
                          "—"
                        )
                      }
                    />
                    <InfoRow
                      label="Next reminder"
                      value={
                        p.next_reminder_at
                          ? fmt.formatDate(p.next_reminder_at, "medium")
                          : "—"
                      }
                    />
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: "var(--color-text-muted)",
                      lineHeight: 1.5,
                    }}
                  >
                    Reminders are friendly notifications only — they never charge
                    your card. You complete each gift yourself through a secure
                    checkout link.
                  </p>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "payments",
        label: "Payment history",
        render: (record, fmt) => {
          const payments = record.payments ?? [];
          if (payments.length === 0) {
            return (
              <p
                data-testid="payments-empty"
                style={{ fontSize: 13, color: "var(--color-text-muted)" }}
              >
                No payments recorded yet.
              </p>
            );
          }
          return (
            <table
              data-testid="payments-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md, 8px)",
                overflow: "hidden",
              }}
            >
              <thead>
                <tr style={{ background: "var(--color-bg-subtle, #f6f7fb)" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 12 }}>
                    Date
                  </th>
                  <th
                    style={{ textAlign: "right", padding: "10px 12px", fontSize: 12 }}
                  >
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ padding: "10px 12px", fontSize: 13 }}>
                      {fmt.formatDate(p.paid_at, "medium")}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        fontSize: 13,
                        textAlign: "right",
                        fontWeight: 500,
                      }}
                    >
                      {fmt.formatCurrency(
                        p.amount_minor_units,
                        record.pledge.currency,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        },
      },
      {
        id: "timeline",
        label: "Timeline",
        render: (record) => <TimelineSection pledgeId={record.pledge.id} />,
      },
    ],
  };

  return <DetailPage config={config} />;
}
